import { handleAxiosError } from '@/api/helpers';
import { Account } from '@/client/accounts';
import { useUpdateAccounts } from '@/hooks/query';
import { useAppDispatch } from '@/hooks/store';
import { useToast } from '@/hooks/useToast';
import { setCurrentAccount } from '@/store/features/account';
import { AppIconTooltip } from '@/ui/icon-tooltip';
import { FlexContainer } from '@/ui/styled-ui';
import { AppSwitch } from '@/ui/switch/AppSwitch';
import { AutoScheduleIcon } from '@/utils/helpers';
import { AxiosError } from 'axios';
import { Tooltip } from 'primereact/tooltip';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledHeader = styled(FlexContainer)`
  padding: var(--medium-big-padding);
  border: 1px solid var(--gray-moderate);
  border-bottom: none;
  border-radius: var(--semi-medium-border-radius)
    var(--semi-medium-border-radius) 0 0;
  background: var(--white-main);
  gap: var(--xsmall-padding);

  .auto-schedule-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 43px;
    height: 43px;
    flex-shrink: 0;
    color: var(--green-color);

    svg {
      width: 16px;
      height: 26px;
    }
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--xxxsmall-padding);
  flex: 1;
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: var(--xxxsmall-padding);
  font-size: var(--small-font-size);
  font-weight: 600;
  color: var(--black-main);
`;

const Description = styled.span`
  font-size: var(--xsmall-font-size);
  font-weight: 400;
  color: var(--black-main);
  line-height: 1.5;
`;

type AutoScheduleHeaderProps = {
  currentAccount?: Account;
  isDemoAccount?: boolean;
};

export const AutoScheduleHeader: React.FC<AutoScheduleHeaderProps> = ({
  currentAccount,
  isDemoAccount,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const dispatch = useAppDispatch();
  const updateAccountMutation = useUpdateAccounts();
  const isEnabled = !!currentAccount?.enableAutoPhishingMode;
  const isToggleDisabled =
    !currentAccount?.isPhishingSenderWhitelisted || isDemoAccount;

  const handleToggle = async (enabled: boolean) => {
    const accountId = currentAccount?.id;
    if (!accountId) return;

    try {
      const updatedAccount = await updateAccountMutation.update({
        accountId,
        updates: { enableAutoPhishingMode: enabled },
      });
      dispatch(
        setCurrentAccount({
          ...currentAccount,
          enableAutoPhishingMode: updatedAccount.enableAutoPhishingMode,
        }),
      );
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  return (
    <StyledHeader justify="flex-start" align="center">
      <div className="auto-schedule-icon">
        <AutoScheduleIcon />
      </div>
      <Content>
        <Title>
          {t('phishing.simulations.autoSchedule.title')}
          <AppIconTooltip
            target="auto-schedule-info"
            content={t('phishing.simulations.autoSchedule.tooltip')}
          />
        </Title>
        <Description>
          {t('phishing.simulations.autoSchedule.description')}
        </Description>
      </Content>
      <div id="auto-schedule-toggle">
        <AppSwitch
          className="v2"
          checked={isEnabled}
          disabled={isToggleDisabled || updateAccountMutation.isLoading}
          onChange={(e) => !isDemoAccount && handleToggle(e.value)}
        />
      </div>
      {!currentAccount?.isPhishingSenderWhitelisted && (
        <Tooltip
          target="#auto-schedule-toggle"
          content={t('phishing.simulations.autoSchedule.whitelistingRequired')}
          position="left"
        />
      )}
    </StyledHeader>
  );
};
